import { useCallback, useState } from 'react';

import { useCookiePrefAsync } from '#src/app/shared/hooks/session';
import { useSessionStorageValueAsync } from '#src/app/shared/hooks/storage';
import { isLoaded } from '#src/app/shared/utils/async';

import { SESSION_FLAG_STORAGE_KEY } from '../../shared/constants';

type Result = Readonly<{
	hasBeenShown: boolean;
	markAsShown: () => void;
}>;

/**
 * Once-per-session *session flag* gate for the toast, respecting cookie preferences (repo critical
 * rule). With `preferences` consent, the flag is read from / written to sessionStorage. Without
 * consent (or before prefs load), it falls back to in-memory state and **nothing is written to
 * browser storage** — the toast may only reappear after a full page load (behavior 20).
 */
export function useToastSessionState(): Result {
	const consentState = useCookiePrefAsync('preferences');
	const hasConsent = isLoaded(consentState) ? consentState.value : false;

	const [storedValue, setStoredValue] = useSessionStorageValueAsync(SESSION_FLAG_STORAGE_KEY);
	const [inMemoryShown, setInMemoryShown] = useState(false);

	const hasBeenShown = hasConsent ? storedValue === 'true' : inMemoryShown;

	const markAsShown = useCallback(() => {
		if (hasConsent) {
			setStoredValue('true');
		} else {
			setInMemoryShown(true);
		}
	}, [hasConsent, setStoredValue]);

	return { hasBeenShown, markAsShown };
}
